<script>
  import { onMount, onDestroy, createEventDispatcher } from 'svelte';
  import { EditorState } from 'prosemirror-state';
  import { EditorView } from 'prosemirror-view';
  import { Schema, DOMParser, DOMSerializer } from 'prosemirror-model';
  import { schema as basicSchema } from 'prosemirror-schema-basic';
  import { addListNodes, wrapInList, liftListItem } from 'prosemirror-schema-list';
  import { exampleSetup } from 'prosemirror-example-setup';
  import { toggleMark, setBlockType } from 'prosemirror-commands';
  import { Bold, Italic, List, ListOrdered, Heading1, Heading2 } from 'lucide-svelte';

  export let value = '';
  export let placeholder = '';
  export let disabled = false;

  const dispatch = createEventDispatcher();

  let editorContainer;
  let editorView = null;

  // Create schema with list support
  const editorSchema = new Schema({
    nodes: addListNodes(basicSchema.spec.nodes, "paragraph block*", "block"),
    marks: basicSchema.spec.marks
  });

  // Parse HTML string to ProseMirror document
  function parseHTML(html) {
    if (!html || html.trim() === '') {
      return editorSchema.node('doc', null, [
        editorSchema.node('paragraph')
      ]);
    }
    const container = document.createElement('div');
    container.innerHTML = html;
    return DOMParser.fromSchema(editorSchema).parse(container);
  }

  // Serialize ProseMirror document to HTML
  function serializeToHTML(doc) {
    const fragment = DOMSerializer.fromSchema(editorSchema).serializeFragment(doc.content);
    const container = document.createElement('div');
    container.appendChild(fragment);
    return container.innerHTML;
  }

  onMount(() => {
    const doc = parseHTML(value);

    const state = EditorState.create({
      doc,
      plugins: exampleSetup({ schema: editorSchema, menuBar: false })
    });

    editorView = new EditorView(editorContainer, {
      state,
      editable: () => !disabled,
      dispatchTransaction(transaction) {
        const newState = editorView.state.apply(transaction);
        editorView.updateState(newState);

        if (transaction.docChanged) {
          const html = serializeToHTML(newState.doc);
          dispatch('change', html);
        }
      },
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[100px] p-3'
      }
    });

    console.log('[RichTextEditor] Initialized with value:', value?.substring(0, 50) || '(empty)');
  });

  onDestroy(() => {
    if (editorView) {
      editorView.destroy();
      editorView = null;
    }
  });

  // Update editor when value prop changes externally
  $: if (editorView && value !== undefined) {
    const currentHTML = serializeToHTML(editorView.state.doc);
    if (currentHTML !== value) {
      const doc = parseHTML(value);
      const newState = EditorState.create({
        doc,
        plugins: editorView.state.plugins
      });
      editorView.updateState(newState);
    }
  }

  // Toggle marks/nodes
  function handleToggleBold() {
    if (!editorView || disabled) return;
    toggleMark(editorSchema.marks.strong)(editorView.state, editorView.dispatch);
    editorView.focus();
  }

  function handleToggleItalic() {
    if (!editorView || disabled) return;
    toggleMark(editorSchema.marks.em)(editorView.state, editorView.dispatch);
    editorView.focus();
  }

  function handleToggleHeading(level) {
    if (!editorView || disabled) return;
    const nodeType = editorSchema.nodes.heading;
    const { $from } = editorView.state.selection;

    // Check if we're already in a heading of this level
    if ($from.parent.type === nodeType && $from.parent.attrs.level === level) {
      // Convert back to paragraph
      setBlockType(editorSchema.nodes.paragraph)(editorView.state, editorView.dispatch);
    } else {
      setBlockType(nodeType, { level })(editorView.state, editorView.dispatch);
    }
    editorView.focus();
  }

  function handleToggleBulletList() {
    if (!editorView || disabled) return;
    const listType = editorSchema.nodes.bullet_list;
    const itemType = editorSchema.nodes.list_item;
    const { $from } = editorView.state.selection;

    // Check if already in list
    const inList = $from.node(-1)?.type === itemType;
    if (inList) {
      liftListItem(itemType)(editorView.state, editorView.dispatch);
    } else {
      wrapInList(listType)(editorView.state, editorView.dispatch);
    }
    editorView.focus();
  }

  function handleToggleOrderedList() {
    if (!editorView || disabled) return;
    const listType = editorSchema.nodes.ordered_list;
    const itemType = editorSchema.nodes.list_item;
    const { $from } = editorView.state.selection;

    // Check if already in list
    const inList = $from.node(-1)?.type === itemType;
    if (inList) {
      liftListItem(itemType)(editorView.state, editorView.dispatch);
    } else {
      wrapInList(listType)(editorView.state, editorView.dispatch);
    }
    editorView.focus();
  }

  // Check active states
  function isMarkActive(markType) {
    if (!editorView) return false;
    const { state } = editorView;
    const { from, $from, to, empty } = state.selection;
    if (empty) {
      return !!markType.isInSet(state.storedMarks || $from.marks());
    }
    return state.doc.rangeHasMark(from, to, markType);
  }

  function isNodeActive(nodeType, attrs = {}) {
    if (!editorView) return false;
    const { $from } = editorView.state.selection;
    for (let d = $from.depth; d >= 0; d--) {
      const node = $from.node(d);
      if (node.type === nodeType) {
        if (Object.keys(attrs).length === 0) return true;
        return Object.entries(attrs).every(([key, val]) => node.attrs[key] === val);
      }
    }
    return false;
  }
</script>

<div class="rich-text-editor border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white dark:bg-gray-800 {disabled ? 'opacity-60' : ''}">
  <!-- Toolbar -->
  <div class="flex items-center gap-1 px-2 py-1.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
    <button
      type="button"
      onclick={handleToggleBold}
      class="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors {disabled ? 'cursor-not-allowed' : ''}"
      disabled={disabled}
      title="Bold (Ctrl+B)"
    >
      <Bold size={16} class="text-gray-700 dark:text-gray-300" />
    </button>

    <button
      type="button"
      onclick={handleToggleItalic}
      class="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors {disabled ? 'cursor-not-allowed' : ''}"
      disabled={disabled}
      title="Italic (Ctrl+I)"
    >
      <Italic size={16} class="text-gray-700 dark:text-gray-300" />
    </button>

    <div class="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1"></div>

    <button
      type="button"
      onclick={() => handleToggleHeading(1)}
      class="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors {disabled ? 'cursor-not-allowed' : ''}"
      disabled={disabled}
      title="Heading 1"
    >
      <Heading1 size={16} class="text-gray-700 dark:text-gray-300" />
    </button>

    <button
      type="button"
      onclick={() => handleToggleHeading(2)}
      class="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors {disabled ? 'cursor-not-allowed' : ''}"
      disabled={disabled}
      title="Heading 2"
    >
      <Heading2 size={16} class="text-gray-700 dark:text-gray-300" />
    </button>

    <div class="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1"></div>

    <button
      type="button"
      onclick={handleToggleBulletList}
      class="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors {disabled ? 'cursor-not-allowed' : ''}"
      disabled={disabled}
      title="Bullet List"
    >
      <List size={16} class="text-gray-700 dark:text-gray-300" />
    </button>

    <button
      type="button"
      onclick={handleToggleOrderedList}
      class="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors {disabled ? 'cursor-not-allowed' : ''}"
      disabled={disabled}
      title="Ordered List"
    >
      <ListOrdered size={16} class="text-gray-700 dark:text-gray-300" />
    </button>
  </div>

  <!-- Editor -->
  <div
    bind:this={editorContainer}
    class="prosemirror-editor"
    data-placeholder={placeholder}
  ></div>
</div>

<style>
  /* ProseMirror base styles */
  :global(.ProseMirror) {
    min-height: 100px;
    padding: 0.75rem;
    outline: none;
  }

  :global(.ProseMirror p) {
    margin: 0 0 0.5rem 0;
  }

  :global(.ProseMirror p:last-child) {
    margin-bottom: 0;
  }

  :global(.ProseMirror h1) {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 0.5rem 0;
  }

  :global(.ProseMirror h2) {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
  }

  :global(.ProseMirror ul, .ProseMirror ol) {
    margin: 0 0 0.5rem 0;
    padding-left: 1.5rem;
  }

  :global(.ProseMirror li) {
    margin-bottom: 0.25rem;
  }

  :global(.ProseMirror strong) {
    font-weight: 600;
  }

  :global(.ProseMirror em) {
    font-style: italic;
  }

  /* Placeholder */
  :global(.ProseMirror p.is-editor-empty:first-child::before) {
    content: attr(data-placeholder);
    color: #9ca3af;
    pointer-events: none;
    float: left;
    height: 0;
  }

  /* Dark mode text color */
  :global(.dark .ProseMirror) {
    color: #e5e7eb;
  }

  :global(.dark .ProseMirror p.is-editor-empty:first-child::before) {
    color: #6b7280;
  }
</style>
